import React, { useCallback, useEffect, useMemo, useState } from "react";
import Heading from "../Components/Heading";
import {
  Box,
  Grid,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Data } from "../Config/Static";
import DateRangePicker from "../Components/DateRange";
import moment from "moment";
import ManageDashboard from "../Components/ManageDashboard";
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { DashboardStyle } from "../Config/Style";
import CustomButton from "../Components/CustomButton";
import SortableItem from "../Components/sortable_item";

const Dashboard = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState("1");
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(Data.data);
  const [openmanage, setOpenmanage] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints?.down("md"));

  // State for managing date range selection
  const [range, setRange] = useState({
    startDate: moment().subtract(6, "days").toDate(),
    endDate: new Date(),
    key: "selection",
  });

  const visibleItems = useMemo(
    () => list.filter((item) => item.display),
    [list]
  );
  const sortableItems = useMemo(
    () => visibleItems.filter((item) => item.id !== "1"),
    [visibleItems]
  );

  // Closes the manage dashboard modal
  const handleClose = () => {
    setOpenmanage(false);
  };

  // Toggles the checkbox state
  const handleCheckboxChange = (id) => {
    setList((prevList) =>
      prevList.map((item) =>
        item.id === id ? { ...item, display: !item.display } : item
      )
    );
  };

  // Collapse all sections in the dashboard
  const handleManage = (btn) => {
    switch (btn) {
      case "Collapse all Widgets":
        setList((prevList) =>
          prevList.map((item) => ({ ...item, expanded: false }))
        );
        break;
      case "Expand all Widgets":
        setList((prevList) =>
          prevList.map((item) => ({ ...item, expanded: true }))
        );
        break;
      case "Reset Layout":
        setList(Data.data);
        break;
      default:
        break;
    }
    handleClose();
  };

  // Configure drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Toggle expansion of an individual section
  const handleExpand = (id) => {
    setList((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, expanded: !item.expanded } : item
      )
    );
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  // Handles drag and drop reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    setList((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      // Prevent Quick Links from being moved or something moved into its place
      if (items[oldIndex].id === "1" || items[newIndex].id === "1") {
        return items;
      }
      return arrayMove(items, oldIndex, newIndex);
    });

    setActiveId(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mt: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          paddingY: { xs: 1, md: 2 },
          paddingX: { xs: 1.5, md: 3 },
          borderRadius: 2,
          mt: 1,
          flexWrap: "wrap",
          rowGap: 1,
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" }, textWrap: "nowrap" }}
        >
          General Overview
        </Typography>
      </Box>

      <Box sx={DashboardStyle.dashboardContainer}>
        <Box sx={DashboardStyle.box}>
          <Box sx={DashboardStyle.btnbox}>
            <CustomButton
              text={"Custom"}
              img={require("../assets/Shape.png")}
              onClick={() => setOpen(!open)}
              border={"black"}
              color={"black"}
              bgcolor={"white"}
              width={"100px"}
            />
            <Typography sx={DashboardStyle.dateTypography}>
              {moment(range.startDate).format("MM/DD/YYYY")}-
              {moment(range.endDate).format("MM/DD/YYYY")}
            </Typography>
          </Box>
          <CustomButton
            text={"Manage Dashboard"}
            color={"black"}
            bgcolor={"#95ABEB"}
            width={"200px"}
            img={require("../assets/filter.png")}
            onClick={() => setOpenmanage(true)}
          />
        </Box>
        <ManageDashboard
          openmanage={openmanage}
          setOpenmanage={setOpenmanage}
          handleManage={handleManage}
          list={list}
          handleCheckboxChange={handleCheckboxChange}
          handleClose={handleClose}
        />
        <DateRangePicker
          open={open}
          range={range}
          setRange={setRange}
          setOpen={setOpen}
        />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* Render Quick Links separately as it's not sortable */}
          {visibleItems.find((item) => item.id === "1") && (
            <SortableItem
              item={visibleItems.find((item) => item.id === "1")}
              handleExpand={handleExpand}
              handleClose={handleCheckboxChange}
            />
          )}

          <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {sortableItems.map((item) => (
                <Grid item xs={12} md={6} key={item.id}>
                  <SortableItem
                    item={item}
                    handleExpand={handleExpand}
                    handleClose={handleCheckboxChange}
                  />
                </Grid>
              ))}
            </Grid>
          </SortableContext>

          <DragOverlay>
            {activeId ? (
              <SortableItem item={list.find((i) => i.id === activeId)} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </Box>
    </Box>
  );
};

export default Dashboard;
